import { useEffect, useRef, useState } from "react";
import { useT } from "../hooks/useT";

export default function MandateSection() {
  const t = useT();
  const ref = useRef(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.05 }
    );
    ref.current?.querySelectorAll(".fade-in, .clip-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section id="mandate" ref={ref} data-testid="mandate-section" className="relative bg-[var(--bg-warm)] border-t" style={{ borderColor: 'var(--rule)' }}>
      <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.4 }}>
        <defs>
          <filter id="mandate-grain" x="0%" y="0%" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="1.2" numOctaves="3" seed="7" stitchTiles="stitch" />
            <feColorMatrix type="saturate" values="0" />
            <feComponentTransfer>
              <feFuncR type="linear" slope="0.03" intercept="0.02" />
              <feFuncG type="linear" slope="0.03" intercept="0.02" />
              <feFuncB type="linear" slope="0.03" intercept="0.02" />
            </feComponentTransfer>
          </filter>
        </defs>
        <rect width="100%" height="100%" filter="url(#mandate-grain)" />
      </svg>

      <div className="max-w-[1400px] mx-auto relative z-[1]">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          {/* Left Sticky Panel */}
          <div className="lg:col-span-4 px-6 md:px-10 py-24 lg:py-0 lg:sticky lg:top-0 lg:h-screen lg:flex lg:flex-col lg:justify-center">
            <div className="fade-in">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-px h-4" style={{ backgroundColor: 'var(--gold)', opacity: 0.3 }} />
                <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-[var(--text-tertiary)]">01</p>
              </div>
              <h2
                data-testid="mandate-heading"
                className="clip-reveal font-serif text-3xl sm:text-4xl lg:text-5xl tracking-[0.06em] text-[var(--text-primary)] mb-10"
                style={{ fontWeight: 300 }}
              >
                {t.mandate.heading}
              </h2>
              <p className="font-serif text-lg text-[var(--text-primary)] leading-relaxed max-w-[320px]" style={{ fontWeight: 300 }}>
                {t.mandate.lead}
              </p>
            </div>
          </div>

          {/* Right Scrollable Panel */}
          <div className="lg:col-span-8 lg:border-l px-6 md:px-10 lg:px-16 py-4 lg:py-44" style={{ borderColor: 'var(--rule)' }}>
            {/* Investment Thesis */}
            <div className="fade-in mb-24">
              <div className="space-y-5 max-w-[600px]">
                {t.mandate.thesis.map((text, i) => (
                  <p key={i} className="font-sans text-[15px] text-[var(--text-secondary)] leading-[1.75]">{text}</p>
                ))}
              </div>
            </div>

            <div className="h-px my-20" style={{ backgroundColor: 'rgba(158, 138, 90, 0.08)' }} />

            {/* Entitlement Sequence */}
            <div className="fade-in mb-24">
              <h3
                data-testid="mandate-entitlement-heading"
                className="font-serif text-2xl sm:text-3xl tracking-[0.04em] text-[var(--text-primary)] mb-16"
                style={{ fontWeight: 300 }}
              >
                {t.mandate.sequenceHeading}
              </h3>
              <div className="space-y-20 lg:border-l lg:pl-8" style={{ borderColor: 'rgba(158, 138, 90, 0.12)' }}>
                {t.mandate.steps.map((step, i) => (
                  <div key={i} className="relative">
                    <span
                      className="absolute -top-4 right-0 font-serif select-none pointer-events-none hidden md:block"
                      style={{
                        fontSize: 'clamp(80px, 8vw, 140px)',
                        color: '#131313',
                        fontWeight: 300,
                        lineHeight: 0.85,
                        transform: `translateY(${(scrollY - 400) * 0.04 * (i + 1) * -0.3}px)`,
                        transition: 'transform 0.1s linear',
                      }}
                    >
                      {step.num}
                    </span>
                    <div className="relative z-10">
                      <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-[var(--text-tertiary)] mb-3">{step.num}</p>
                      <h4 className="font-serif text-xl tracking-[0.03em] text-[var(--text-primary)] mb-3" style={{ fontWeight: 300 }}>{step.label}</h4>
                      <p className="font-sans text-[15px] text-[var(--text-secondary)] leading-[1.75] max-w-[500px]">{step.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="font-sans text-[15px] text-[var(--text-secondary)] max-w-[500px] leading-[1.75] mt-16">
                {t.mandate.sequenceNote}
              </p>
            </div>

            <div className="h-px my-20" style={{ backgroundColor: 'rgba(158, 138, 90, 0.08)' }} />

            {/* Discipline */}
            <div className="fade-in mb-24">
              <h3
                data-testid="mandate-discipline-heading"
                className="font-serif text-2xl sm:text-3xl tracking-[0.04em] text-[var(--text-primary)] mb-10"
                style={{ fontWeight: 300 }}
              >
                {t.mandate.disciplineHeading}
              </h3>
              <div className="space-y-5 max-w-[600px]">
                {t.mandate.discipline.map((text, i) => (
                  <p key={i} className="font-sans text-[15px] text-[var(--text-secondary)] leading-[1.75]">{text}</p>
                ))}
              </div>
            </div>

            <div className="h-px my-20" style={{ backgroundColor: 'rgba(158, 138, 90, 0.08)' }} />

            {/* Risk & Horizon */}
            <div className="fade-in">
              <h3
                data-testid="mandate-risk-heading"
                className="font-serif text-2xl sm:text-3xl tracking-[0.04em] text-[var(--text-primary)] mb-10"
                style={{ fontWeight: 300 }}
              >
                {t.mandate.riskHeading}
              </h3>
              <div className="space-y-5 max-w-[600px]">
                {t.mandate.risk.map((text, i) => (
                  <p key={i} className="font-sans text-[15px] text-[var(--text-secondary)] leading-[1.75]">{text}</p>
                ))}
                <p className="font-sans text-[15px] text-[var(--text-primary)] leading-[1.75] mt-8 italic">
                  {t.mandate.riskCallout}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
